import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_URL from '../config/api';
import { 
  FiUser, 
  FiMail, 
  FiShield, 
  FiStar, 
  FiKey, 
  FiCheckCircle,
  FiXCircle,
  FiEdit2,
  FiSave,
  FiX,
  FiLock,
  FiEye,
  FiEyeOff,
  FiRefreshCw,
  FiAlertCircle,
  FiClock,
  FiCalendar,
  FiActivity,
  FiCpu,
  FiSmartphone,
  FiGlobe,
  FiLogOut
} from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, isOwner, isAdmin, adminType, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [activityLog, setActivityLog] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({
    totalClients: 0,
    actionsToday: 0,
    lastLogin: null,
    accountAge: 0
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || ''
      });
      fetchActivityLog();
      fetchUserStats();
    }
  }, [user]);

  const fetchActivityLog = async () => {
    try {
      // Simular actividad reciente (esto debería venir del backend)
      const mockActivity = [
        {
          id: 1,
          action: 'Inicio de sesión',
          timestamp: new Date(Date.now() - 30 * 60000),
          ip: '192.168.1.100',
          device: 'Chrome en Windows'
        },
        {
          id: 2,
          action: 'Actualización de cliente #1005',
          timestamp: new Date(Date.now() - 2 * 60 * 60000),
          ip: '192.168.1.100',
          device: 'Chrome en Windows'
        },
        {
          id: 3,
          action: 'Exportación de reporte',
          timestamp: new Date(Date.now() - 5 * 60 * 60000),
          ip: '192.168.1.100',
          device: 'Chrome en Windows'
        },
        {
          id: 4,
          action: 'Cambio de estado de luz',
          timestamp: new Date(Date.now() - 24 * 60 * 60000),
          ip: '192.168.1.100',
          device: 'Chrome en Windows'
        },
        {
          id: 5,
          action: 'Creación de tarea',
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60000),
          ip: '192.168.1.100',
          device: 'Chrome en Windows'
        }
      ];
      setActivityLog(mockActivity);
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  const fetchUserStats = async () => {
    try {
      // Obtener estadísticas del usuario
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: null })); // Si falla, usamos datos simulados

      if (response.data) {
        setStats(response.data);
      } else {
        // Datos simulados
        const createdAt = user?.createdAt || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        const accountAge = Math.floor((Date.now() - new Date(createdAt)) / (24 * 60 * 60 * 1000));
        
        setStats({
          totalClients: 156,
          actionsToday: 23,
          lastLogin: new Date(Date.now() - 45 * 60000),
          accountAge
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/users/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Perfil actualizado correctamente');
      setEditMode(false);
      
      // Actualizar el usuario en el contexto (simulado)
      setTimeout(() => {
        window.location.reload(); // Recargar para actualizar contexto
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/users/password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Contraseña actualizada correctamente');
      setChangePassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.message || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAll = async () => {
    if (window.confirm('¿Estás seguro de cerrar sesión en todos los dispositivos?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API_URL}/api/auth/logout-all`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sesión cerrada en todos los dispositivos');
        logout();
      } catch (error) {
        console.error('Error logging out all:', error);
        toast.error('Error al cerrar sesiones');
      }
    }
  };

  const getRoleBadge = () => {
    if (isOwner) {
      return (
        <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full flex items-center text-sm">
          <FiStar className="mr-1" size={14} />
          Dueño / Owner
        </div>
      );
    }
    if (isAdmin) {
      return (
        <div className={`px-3 py-1 rounded-full flex items-center text-sm ${
          adminType === 'Principal' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          <FiShield className="mr-1" size={14} />
          Admin {adminType}
        </div>
      );
    }
    return (
      <div className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full flex items-center text-sm">
        <FiUser className="mr-1" size={14} />
        Usuario
      </div>
    );
  };

  const PermissionItem = ({ label, hasPermission, description }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <span className="text-gray-700 font-medium">{label}</span>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
      {hasPermission ? (
        <FiCheckCircle className="text-green-500" size={20} />
      ) : (
        <FiXCircle className="text-red-400" size={20} />
      )}
    </div>
  );

  const formatTime = (date) => {
    if (!date) return 'N/A';
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale: es });
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Mi Perfil</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            Gestiona tu información personal y seguridad
          </p>
        </div>
        <div className="flex gap-2">
          {!editMode && !changePassword && (
            <button
              onClick={() => setEditMode(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit2 className="mr-2" size={16} />
              Editar Perfil
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna izquierda - Información básica */}
        <div className="lg:col-span-1 space-y-6">
          {/* Tarjeta de perfil */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-white text-center">
              <div className="w-24 h-24 bg-white rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                <FiUser className="text-blue-600 text-4xl" />
              </div>
              <h2 className="text-xl font-bold">{user?.name}</h2>
              <p className="text-blue-100 text-sm mt-1">{user?.email}</p>
              <div className="mt-3 flex justify-center">
                {getRoleBadge()}
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">ID de Usuario</label>
                  <p className="text-sm font-mono bg-gray-50 p-2 rounded mt-1">{user?._id || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Miembro desde</label>
                  <p className="text-sm flex items-center mt-1">
                    <FiCalendar className="mr-2 text-gray-400" size={14} />
                    {new Date(user?.createdAt || Date.now()).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Último acceso</label>
                  <p className="text-sm flex items-center mt-1">
                    <FiClock className="mr-2 text-gray-400" size={14} />
                    {formatTime(stats.lastLogin)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Estadísticas rápidas */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center">
              <FiActivity className="mr-2 text-blue-600" size={18} />
              Estadísticas
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.totalClients}</p>
                <p className="text-xs text-gray-600">Clientes totales</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.actionsToday}</p>
                <p className="text-xs text-gray-600">Acciones hoy</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center col-span-2">
                <p className="text-2xl font-bold text-purple-600">{stats.accountAge}</p>
                <p className="text-xs text-gray-600">Días en la plataforma</p>
              </div>
            </div>
          </div>
        </div>

        {/* Columna derecha - Información detallada */}
        <div className="lg:col-span-2 space-y-6">
          {/* Formulario de edición */}
          {editMode ? (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Editar Perfil</h3>
                <button
                  onClick={() => setEditMode(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <FiRefreshCw className="animate-spin mr-2" size={16} />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FiSave className="mr-2" size={16} />
                        Guardar cambios
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditMode(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          ) : changePassword ? (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Cambiar Contraseña</h3>
                <button
                  onClick={() => setChangePassword(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={20} />
                </button>
              </div>
              
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña actual
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    >
                      {showCurrentPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nueva contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar nueva contraseña
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <FiRefreshCw className="animate-spin mr-2" size={16} />
                        Cambiando...
                      </>
                    ) : (
                      <>
                        <FiLock className="mr-2" size={16} />
                        Cambiar contraseña
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setChangePassword(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <>
              {/* Permisos */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FiKey className="mr-2 text-blue-600" size={18} />
                  Permisos del Sistema
                </h3>
                
                <div className="space-y-1">
                  <PermissionItem 
                    label="Gestionar Usuarios" 
                    hasPermission={isOwner}
                    description="Crear, editar y eliminar usuarios"
                  />
                  <PermissionItem 
                    label="Eliminar Clientes" 
                    hasPermission={isOwner}
                    description="Eliminar clientes permanentemente"
                  />
                  <PermissionItem 
                    label="Exportar Datos" 
                    hasPermission={isOwner || user?.permissions?.canExportData}
                    description="Exportar reportes y listados"
                  />
                  <PermissionItem 
                    label="Ver ZIDCode" 
                    hasPermission={isOwner || user?.permissions?.canViewZidCode}
                    description="Acceder a códigos ZID de clientes"
                  />
                  <PermissionItem 
                    label="Gestionar Estado de Luz" 
                    hasPermission={isOwner || user?.permissions?.canManagePowerStatus}
                    description="Cambiar estado de luz de clientes"
                  />
                  <PermissionItem 
                    label="Marcar como Urgente" 
                    hasPermission={isOwner || user?.permissions?.canMarkUrgent}
                    description="Marcar clientes como urgentes"
                  />
                  <PermissionItem 
                    label="Gestionar Cancelaciones" 
                    hasPermission={isOwner || user?.permissions?.canManageCancellations}
                    description="Procesar cancelaciones de clientes"
                  />
                  <PermissionItem 
                    label="Crear Clientes" 
                    hasPermission={true}
                    description="Registrar nuevos clientes"
                  />
                  <PermissionItem 
                    label="Procesar Pagos" 
                    hasPermission={true}
                    description="Registrar pagos de clientes"
                  />
                  <PermissionItem 
                    label="Ver Reportes" 
                    hasPermission={true}
                    description="Acceder a reportes y estadísticas"
                  />
                </div>

                {isAdmin && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700">
                      <strong className="block mb-1">ℹ️ Información de Admin</strong>
                      {adminType === 'Principal' 
                        ? 'Como Admin Principal, tienes permisos especiales de exportación y gestión.'
                        : 'Como Admin Secundario, tienes permisos limitados. Contacta al Admin Principal para cambios.'}
                    </p>
                  </div>
                )}

                {isOwner && (
                  <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-700">
                      <strong className="block mb-1">👑 Acceso de Dueño</strong>
                      Tienes acceso completo a todas las funcionalidades del sistema. Puedes gestionar usuarios, permisos y configuración global.
                    </p>
                  </div>
                )}
              </div>

              {/* Seguridad */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FiLock className="mr-2 text-blue-600" size={18} />
                  Seguridad
                </h3>

                <div className="space-y-4">
                  <button
                    onClick={() => setChangePassword(true)}
                    className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center">
                      <FiLock className="text-blue-600 mr-3" size={20} />
                      <div className="text-left">
                        <p className="font-medium text-gray-700">Cambiar contraseña</p>
                        <p className="text-xs text-gray-500">Actualiza tu contraseña regularmente</p>
                      </div>
                    </div>
                    <FiEdit2 className="text-gray-400" size={18} />
                  </button>

                  <button
                    onClick={handleLogoutAll}
                    className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 transition-colors group"
                  >
                    <div className="flex items-center">
                      <FiLogOut className="text-red-500 mr-3" size={20} />
                      <div className="text-left">
                        <p className="font-medium text-gray-700 group-hover:text-red-700">Cerrar sesión en todos los dispositivos</p>
                        <p className="text-xs text-gray-500">Termina todas las sesiones activas</p>
                      </div>
                    </div>
                    <FiXCircle className="text-gray-400 group-hover:text-red-500" size={18} />
                  </button>
                </div>
              </div>

              {/* Actividad reciente */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FiActivity className="mr-2 text-blue-600" size={18} />
                  Actividad Reciente
                </h3>

                <div className="space-y-3">
                  {activityLog.map((activity) => (
                    <div key={activity.id} className="flex items-start p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 mr-3"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{activity.action}</p>
                        <div className="flex items-center text-xs text-gray-500 mt-1 space-x-2">
                          <span className="flex items-center">
                            <FiClock className="mr-1" size={12} />
                            {formatTime(activity.timestamp)}
                          </span>
                          <span>•</span>
                          <span className="flex items-center">
                            <FiCpu className="mr-1" size={12} />
                            {activity.device}
                          </span>
                          <span>•</span>
                          <span className="flex items-center">
                            <FiGlobe className="mr-1" size={12} />
                            {activity.ip}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;